import { useRef } from 'react';

import { useTranslation } from 'react-i18next';
import {
  A11y,
  Autoplay,
  FreeMode,
  Grid,
  Navigation,
  Pagination,
  Mousewheel,
  Keyboard,
  Scrollbar,
} from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/grid';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';

import SliderFooter from '@/components/Slider/components/SliderFooter/SliderFooter';
import useAutoplayController from '@/components/Slider/hooks/useAutoplayController';
import useSliderEdges from '@/components/Slider/hooks/useSliderEdges';
import useSwiperRefBindings from '@/components/Slider/hooks/useSwiperRefBindings';

import styles from './styles.module.css';

import type { SliderProps } from './types';
import type {
  AutoplayOptions as SwiperAutoplayOptions,
  PaginationOptions as SwiperPaginationOptions,
  Swiper as SwiperClass,
} from 'swiper/types';

const resolveAutoplayConfig = (autoplay: SliderProps['autoplay']): SwiperAutoplayOptions | false => {
  if (!autoplay) return false;
  if (typeof autoplay === 'boolean') return { delay: 5000, disableOnInteraction: false };

  return {
    delay: autoplay.delay ?? 5000,
    disableOnInteraction: autoplay.disableOnInteraction ?? false,
  };
};

const resolvePaginationConfig = (showPagination: SliderProps['showPagination']): SwiperPaginationOptions | false => {
  if (!showPagination) return false;
  if (typeof showPagination === 'string') return { type: showPagination, bulletElement: 'button' };
  if (typeof showPagination === 'boolean') return { type: 'bullets', clickable: true, bulletElement: 'button' };

  return {
    type: showPagination.type ?? 'bullets',
    clickable: showPagination.clickable ?? true,
    dynamicBullets: showPagination.dynamicBullets ?? false,
    dynamicMainBullets: showPagination.dynamicMainBullets,
    hideOnClick: showPagination.hideOnClick ?? false,
    bulletElement: 'button',
  };
};

const Slider = ({
  slides,
  direction = 'horizontal',
  slidesPerView = 1,
  spaceBetween = 16,
  rows = 1,
  loop = false,
  showNavigation = true,
  showPagination,
  showScrollbar = false,
  autoplay = false,
  breakpoints,
  height,
  className,
  initialSlide,
  showPlayPause,
  freeMode = false,
}: SliderProps) => {
  const { t } = useTranslation();
  const swiperRef = useRef<SwiperClass | null>(null);
  const prevRef = useRef<HTMLButtonElement | null>(null);
  const nextRef = useRef<HTMLButtonElement | null>(null);
  const paginationRef = useRef<HTMLDivElement | null>(null);

  const autoplayConfig = resolveAutoplayConfig(autoplay);

  const paginationConfig = resolvePaginationConfig(showPagination);
  const isPaginationEnabled = paginationConfig !== false && slides.length >= 3;
  const isAutoplayEnabled = autoplayConfig !== false;
  const shouldShowPlayPause = showPlayPause ?? isAutoplayEnabled;
  const shouldShowFooter = slides.length >= 3 && (isPaginationEnabled || showNavigation || shouldShowPlayPause);

  const hasMultiRowGrid =
    rows > 1 || Object.values(breakpoints ?? {}).some((breakpoint) => (breakpoint?.grid?.rows ?? 1) > 1);
  const effectiveLoop = loop && !hasMultiRowGrid;

  const { isAtBeginning, isAtEnd, updateEdges } = useSliderEdges();
  const {
    isAutoplayRunning,
    toggleAutoplayHandler,
    setIsAutoplayRunning,
    setToggleAutoplayHandler,
    AutoplayController,
  } = useAutoplayController(isAutoplayEnabled);

  useSwiperRefBindings({
    swiperRef,
    prevRef,
    nextRef,
    paginationRef,
    showNavigation,
    isPaginationEnabled,
    showPagination,
  });

  return (
    <div className={`${styles.container} ${className ?? ''}`}>
      <div className={styles.sliderContainer}>
        <Swiper
          modules={[Navigation, Pagination, Autoplay, FreeMode, Grid, A11y, Mousewheel, Keyboard, Scrollbar]}
          mousewheel={{ forceToAxis: true }}
          keyboard={{ enabled: true, onlyInViewport: true }}
          a11y={{
            enabled: true,
            prevSlideMessage: t('slider.previousSlide'),
            nextSlideMessage: t('slider.nextSlide'),
            paginationBulletMessage: `${t('slider.goToSlide')} {{index}}`,
          }}
          direction={direction}
          slidesPerView={slidesPerView}
          spaceBetween={spaceBetween}
          grid={{ rows, fill: 'column' }}
          loop={effectiveLoop}
          breakpoints={breakpoints}
          autoHeight={!height}
          autoplay={autoplayConfig}
          scrollbar={showScrollbar ? { draggable: true, hide: true } : false}
          pagination={paginationConfig}
          navigation={showNavigation ? { prevEl: prevRef.current, nextEl: nextRef.current } : false}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            updateEdges(swiper);
          }}
          onSlideChange={(swiper) => {
            updateEdges(swiper);
          }}
          onBeforeInit={(swiper) => {
            if (showNavigation && typeof swiper.params.navigation === 'object' && swiper.params.navigation) {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
            }
            if (isPaginationEnabled && typeof swiper.params.pagination === 'object' && swiper.params.pagination) {
              swiper.params.pagination.el = paginationRef.current;
            }
          }}
          className={styles.swiper}
          style={height ? { height: typeof height === 'number' ? `${height}px` : height } : undefined}
          freeMode={freeMode}
          initialSlide={initialSlide}
        >
          {slides.map((slide) => (
            <SwiperSlide key={slide.id} className={styles.slide}>
              {slide.content}
            </SwiperSlide>
          ))}
          <AutoplayController
            isAutoplayEnabled={isAutoplayEnabled}
            setToggleHandler={setToggleAutoplayHandler}
            setIsAutoplayRunning={setIsAutoplayRunning}
          />
        </Swiper>
      </div>

      <div
        aria-hidden="true"
        className={`${styles.edgeFade} ${styles.edgeFadeLeft} ${!effectiveLoop && isAtBeginning ? styles.hideLeftFade : ''}`}
      />
      <div
        aria-hidden="true"
        className={`${styles.edgeFade} ${styles.edgeFadeRight} ${!effectiveLoop && isAtEnd ? styles.hideRightFade : ''}`}
      />
      {shouldShowFooter && (
        <SliderFooter
          showNavigation={showNavigation}
          isPaginationEnabled={isPaginationEnabled}
          shouldShowPlayPause={shouldShowPlayPause}
          isAutoplayRunning={isAutoplayRunning}
          isAutoplayEnabled={isAutoplayEnabled}
          toggleAutoplayHandler={toggleAutoplayHandler}
          prevRef={prevRef}
          nextRef={nextRef}
          paginationRef={paginationRef}
        />
      )}
    </div>
  );
};

export default Slider;
